const SOURCE_SEGMENTS = new Set(['body', 'query', 'path', 'header', 'cookie']);

// Segments that are pure structural noise — Pydantic's root-validator marker
// or BE wrappers whose name adds nothing for the user (e.g. there's only one
// `sort_code` field, so prefixing it with "Payout Details" is just clutter).
const HIDDEN_SEGMENTS = new Set(['__root__', 'payout_details']);

const FIELD_NAME_OVERRIDES = {
  iban: 'IBAN',
  id: 'ID',
  ip: 'IP',
  api: 'API',
  url: 'URL',
  uri: 'URI',
  uuid: 'UUID',
  vat: 'VAT',
  uk: 'UK',
  eu: 'EU',
  kyc: 'KYC',
  kyb: 'KYB',
  dob: 'Date of Birth',
  tos_agreement: 'Terms of Service',
};

const titleCase = (word) => {
  if (!word) {
    return '';
  }
  const lower = word.toLowerCase();
  if (FIELD_NAME_OVERRIDES[lower]) {
    return FIELD_NAME_OVERRIDES[lower];
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const humaniseSegment = (segment) => {
  if (!segment) {
    return '';
  }
  const override = FIELD_NAME_OVERRIDES[segment.toLowerCase()];
  if (override) {
    return override;
  }

  const words = segment
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .split(/[_\s]+/)
    .filter(Boolean);

  return words.map(titleCase).join(' ');
};

// Pydantic model class names sometimes appear in the loc (e.g.
// `UpdateIndividualAccountDraftRequest`). They start with an uppercase letter;
// real BE field names are snake_case (or camelCase passthroughs that start
// lowercase). Strip any segment that looks like a class name.
const isPydanticClassName = (s) => typeof s === 'string' && /^[A-Z]/.test(s);

export const humaniseFieldName = (loc) => {
  if (!Array.isArray(loc) || loc.length === 0) {
    return '';
  }

  let segments =
    loc[0] != null && SOURCE_SEGMENTS.has(String(loc[0]).toLowerCase())
      ? loc.slice(1)
      : loc;

  segments = segments.filter(
    (s) =>
      !HIDDEN_SEGMENTS.has(String(s ?? '').toLowerCase()) &&
      !isPydanticClassName(s)
  );

  const parts = [];
  for (const seg of segments) {
    const isIndex = typeof seg === 'number' || /^\d+$/.test(String(seg ?? ''));
    if (isIndex) {
      const display = `#${parseInt(seg, 10) + 1}`;
      if (parts.length > 0) {
        parts[parts.length - 1] = `${parts[parts.length - 1]} ${display}`;
      } else {
        parts.push(display);
      }
    } else if (seg != null && seg !== '') {
      parts.push(humaniseSegment(String(seg)));
    }
  }

  return parts.filter(Boolean).join(' ');
};

const num = (ctx, ...keys) => {
  if (!ctx) {
    return null;
  }
  for (const k of keys) {
    if (ctx[k] != null) {
      return ctx[k];
    }
  }
  return null;
};

const formatPermitted = (ctx) => {
  if (!ctx) {
    return null;
  }
  const value = ctx.permitted ?? ctx.expected;
  if (Array.isArray(value)) {
    return value.map((v) => `'${v}'`).join(', ');
  }
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return null;
};

const tooShort = (n) =>
  n != null ? `is too short (minimum ${n} characters)` : 'is too short';
const tooLong = (n) =>
  n != null ? `is too long (maximum ${n} characters)` : 'is too long';
const atLeast = (n) => (n != null ? `must be at least ${n}` : 'is too small');
const greaterThan = (n) =>
  n != null ? `must be greater than ${n}` : 'is too small';
const atMost = (n) => (n != null ? `must be at most ${n}` : 'is too large');
const lessThan = (n) => (n != null ? `must be less than ${n}` : 'is too large');
const minItems = (n) =>
  n != null ? `requires at least ${n} item(s)` : 'requires more items';
const maxItems = (n) =>
  n != null ? `must have at most ${n} item(s)` : 'has too many items';

const enumMessage = (ctx) => {
  const p = formatPermitted(ctx);
  return p ? `must be one of: ${p}` : 'has an invalid value';
};

const literalMessage = (ctx) => {
  const p = formatPermitted(ctx);
  return p ? `must be ${p}` : 'has an invalid value';
};

const MESSAGE_MAP = {
  // Required
  'value_error.missing': () => 'is required',
  missing: () => 'is required',
  'type_error.none.not_allowed': () => 'is required',
  none_required: () => 'is required',

  // String length
  'value_error.any_str.min_length': (ctx) => tooShort(num(ctx, 'limit_value')),
  string_too_short: (ctx) => tooShort(num(ctx, 'min_length')),
  'value_error.any_str.max_length': (ctx) => tooLong(num(ctx, 'limit_value')),
  string_too_long: (ctx) => tooLong(num(ctx, 'max_length')),

  // Pattern
  'value_error.str.regex': () => 'is in an invalid format',
  string_pattern_mismatch: () => 'is in an invalid  format',

  // Email
  'value_error.email': () => 'must be a valid email address',

  // URL
  url_parsing: () => 'must be a valid URL',
  url_scheme: () => 'must be a valid URL',
  url_type: () => 'must be a valid URL',

  // Literal / Enum
  'value_error.const': literalMessage,
  literal_error: literalMessage,
  'value_error.enum': enumMessage,
  'type_error.enum': enumMessage,
  enum: enumMessage,

  // Number type
  'type_error.integer': () => 'must be a number',
  int_type: () => 'must be a number',
  int_parsing: () => 'must be a number',
  'type_error.float': () => 'must be a number',
  'type_error.number.not_finite': () => 'must be a number',
  float_type: () => 'must be a number',
  float_parsing: () => 'must be a number',

  // Bool
  'type_error.bool': () => 'must be true or false',
  bool_type: () => 'must be true or false',
  bool_parsing: () => 'must be true or false',

  // Numeric bounds
  'value_error.number.not_ge': (ctx) => atLeast(num(ctx, 'limit_value')),
  greater_than_equal: (ctx) => atLeast(num(ctx, 'ge')),
  'value_error.number.not_gt': (ctx) => greaterThan(num(ctx, 'limit_value')),
  greater_than: (ctx) => greaterThan(num(ctx, 'gt')),
  'value_error.number.not_le': (ctx) => atMost(num(ctx, 'limit_value')),
  less_than_equal: (ctx) => atMost(num(ctx, 'le')),
  'value_error.number.not_lt': (ctx) => lessThan(num(ctx, 'limit_value')),
  less_than: (ctx) => lessThan(num(ctx, 'lt')),

  // List bounds
  'value_error.list.min_items': (ctx) => minItems(num(ctx, 'limit_value')),
  too_short: (ctx) => minItems(num(ctx, 'min_length')),
  'value_error.list.max_items': (ctx) => maxItems(num(ctx, 'limit_value')),
  too_long: (ctx) => maxItems(num(ctx, 'max_length')),

  // Date / datetime
  'value_error.date': () => 'must be a valid date',
  date_parsing: () => 'must be a valid date',
  date_type: () => 'must be a valid date',
  'value_error.datetime': () => 'must be a valid date and time',
  datetime_parsing: () => 'must be a valid date and time',
  datetime_type: () => 'must be a valid date and time',

  // UUID
  'value_error.uuid': () => 'must be a valid identifier',
  uuid_parsing: () => 'must be a valid identifier',
  uuid_type: () => 'must be a valid identifier',

  // Object
  'value_error.dict': () => 'must be an object',
  dict_type: () => 'must be an object',
};

const cleanupRawMessage = (msg) => {
  if (!msg || typeof msg !== 'string') {
    return 'is invalid';
  }
  let out = msg.trim();
  out = out.replace(/^Input should be /i, 'must be ');
  out = out.replace(/^Value error,?\s*/i, '');
  out = out.replace(/\.\s*$/, '');
  if (out.length === 0) {
    return 'is invalid';
  }
  return out.charAt(0).toLowerCase() + out.slice(1);
};

export const humaniseValidationMessage = (error) => {
  if (!error) {
    return 'is invalid';
  }
  const factory = MESSAGE_MAP[error.type];
  if (factory) {
    return factory(error.ctx);
  }
  return cleanupRawMessage(error.msg);
};

/**
 * Format a Pydantic 422 `detail` array into a single user-readable description
 * string, ready to drop into `notification.error({ description })`. Returns
 * `null` if the input isn't a valid Pydantic detail array, or if anything
 * throws internally — callers should fall back to a generic message in that
 * case.
 *
 * Handles Pydantic v1 (`type_error.none.not_allowed`, etc.) and Pydantic v2
 * (`missing`, `string_too_short`, etc.) keyed by intent.
 *
 * @param {Array<{loc?: any[], msg?: string, type?: string, ctx?: object}>} detail
 * @returns {string|null}
 */
export const format422ApiError = (detail) => {
  try {
    if (!Array.isArray(detail) || detail.length === 0) {
      return null;
    }

    const seen = new Set();
    const items = [];

    for (const error of detail) {
      if (!error || typeof error !== 'object') {
        continue;
      }
      if (!error.loc && !error.msg) {
        continue;
      }

      const field = humaniseFieldName(error.loc);
      const message = humaniseValidationMessage(error);
      if (!field && !message) {
        continue;
      }

      const key = `${field}::${message}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      items.push(field ? `${field} ${message}` : message);
    }

    if (items.length === 0) {
      return null;
    }
    return items.join('. ');
  } catch {
    return null;
  }
};
